import { Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import ModalV2 from '/@/components/bayer/modal-v2';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { parseQuerystring, reverse, stringifyQuerystring } from '/@/utils/router';

const SuccessDialog = ({
    fieldId,
    nextUrl,
    onCreateAnotherFieldClick,
}: {
    fieldId: number;
    nextUrl?: string | null;
    onCreateAnotherFieldClick: () => void;
}) => {
    const navigate = useNavigate();

    const isRv = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();

    const fieldQuery = useFieldById({ id: fieldId });
    const growerNode = useGetUserNode(fieldQuery.data?.userNode ?? 0);
    const ownUserNode = useOwnUserNode();
    const isOwnUser = ownUserNode.data?.id === fieldQuery.data?.userNode;

    const handleNavigate = () => {
        if (nextUrl) {
            // If nextUrl is provided, use it and add query params
            const decodedNextUrl = decodeURIComponent(nextUrl);
            const [pathname, search] = decodedNextUrl.split('?');
            const existingParams = search ? parseQuerystring(`?${search}`) : {};

            // If the URL contains fieldIds, replace it with only the new field ID
            // Otherwise, add selectedField as before
            const newParams = {
                ...existingParams,
            };

            if ('fieldIds' in existingParams) {
                newParams.fieldIds = fieldId.toString();
            } else {
                newParams.selectedField = fieldId.toString();
            }

            const newSearch = stringifyQuerystring(newParams);
            const finalUrl = `${pathname}${newSearch ? `?${newSearch}` : ''}`;
            navigate(finalUrl);
        } else {
            if (isOwnUser) {
                navigate(
                    reverse('MyFields.list', {
                        qs: { selectedField: fieldId },
                    })
                );
            } else if (isRv || isAdvisor) {
                navigate(reverse('Growers.list', { qs: { selectedField: fieldId } }));
            } else {
                navigate(
                    reverse('Home', {
                        qs: { selectedField: fieldId },
                    })
                );
            }
        }
    };

    const onClose = handleNavigate;

    return (
        <ModalV2
            open={Boolean(fieldId)}
            onClose={onClose}
            title={_('¡Lote agregado con éxito!')}
            actionsProps={{
                sx: {
                    justifyContent: 'flex-end',
                },
            }}
            data-testid='success-dialog-button-add-another-field'
            actions={
                <>
                    <Button
                        variant='text'
                        color='secondary'
                        onClick={() => {
                            onCreateAnotherFieldClick();
                        }}
                    >
                        {_('Agregar otro lote')}
                    </Button>
                    <Button variant='contained' color='secondary' onClick={onClose}>
                        {_('Volver')}
                    </Button>
                </>
            }
            bodyProps={{
                spacing: 3,
            }}
        >
            <Typography variant='body1' fontWeight='bold' color='primary'>
                {growerNode?.data?.isSelf || !isRv
                    ? _('El lote ya está disponible en {{appName}}.', { appName: settings.app.name })
                    : _('El lote de {{growerName}} ya está disponible en {{appName}}.', {
                          growerName: growerNode?.data?.name,
                          appName: settings.app.name,
                      })}
            </Typography>
            <Typography variant='body1' color='primary'>
                {_('Ahora podés seguir sus campañas y generar recomendaciones según el momento del año.')}
            </Typography>
        </ModalV2>
    );
};

export default SuccessDialog;
